import { createSlice } from '@reduxjs/toolkit';

const MAX_HISTORY_SIZE = 20;

const defaultState = {
  currentDesign: null,
  product: null,
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
  material: 'kraft',
  customColor: null, // For custom color picker
  printArea: 'full_color',
  designFile: null,
  isModified: false,
  validationResults: null,
  showValidation: true,
  // Print overlay visibility
  showOverlays: true,
  showBleed: true,
  showSafeZone: true,
  showCutLines: true,
  // Text overlays
  textLayers: [],
  activeTextLayerId: null,
  // Camera presets
  cameraPreset: 'default',
  // Templates
  selectedTemplate: null,
};

const initialState = {
  ...defaultState,
  // Undo/Redo history
  history: [],
  historyIndex: -1,
  canUndo: false,
  canRedo: false,
};

// Helper to get state snapshot for history
const getStateSnapshot = (state) => ({
  position: { ...state.position },
  rotation: { ...state.rotation },
  scale: { ...state.scale },
  material: state.material,
  customColor: state.customColor,
  printArea: state.printArea,
  textLayers: state.textLayers.map(layer => ({ ...layer })),
  activeTextLayerId: state.activeTextLayerId,
});

// Helper to restore state from snapshot
const restoreFromSnapshot = (state, snapshot) => {
  state.position = { ...snapshot.position };
  state.rotation = { ...snapshot.rotation };
  state.scale = { ...snapshot.scale };
  state.material = snapshot.material;
  state.customColor = snapshot.customColor;
  state.printArea = snapshot.printArea;
  state.textLayers = snapshot.textLayers.map(layer => ({ ...layer }));
  state.activeTextLayerId = snapshot.activeTextLayerId;
};

// Helper to push state to history
const pushToHistory = (state) => {
  const snapshot = getStateSnapshot(state);

  // Remove any future history if we're not at the end
  if (state.historyIndex < state.history.length - 1) {
    state.history = state.history.slice(0, state.historyIndex + 1);
  }

  // Add new snapshot
  state.history.push(snapshot);

  // Limit history size
  if (state.history.length > MAX_HISTORY_SIZE) {
    state.history = state.history.slice(-MAX_HISTORY_SIZE);
  }

  state.historyIndex = state.history.length - 1;
  state.canUndo = state.historyIndex > 0;
  state.canRedo = false;
  state.isModified = true;
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    setProduct: (state, action) => {
      state.product = action.payload;
    },
    setPosition: (state, action) => {
      pushToHistory(state);
      state.position = action.payload;
    },
    setRotation: (state, action) => {
      pushToHistory(state);
      state.rotation = action.payload;
    },
    setScale: (state, action) => {
      pushToHistory(state);
      state.scale = action.payload;
    },
    setMaterial: (state, action) => {
      pushToHistory(state);
      state.material = action.payload;
      state.customColor = null; // Reset custom color when selecting preset material
    },
    setCustomColor: (state, action) => {
      pushToHistory(state);
      state.customColor = action.payload;
      state.material = 'custom'; // Set material to custom when using color picker
    },
    setPrintArea: (state, action) => {
      pushToHistory(state);
      state.printArea = action.payload;
    },
    setDesignFile: (state, action) => {
      state.designFile = action.payload;
      state.isModified = true;
    },
    setValidationResults: (state, action) => {
      state.validationResults = action.payload;
    },
    toggleValidation: (state) => {
      state.showValidation = !state.showValidation;
    },
    toggleOverlays: (state) => {
      state.showOverlays = !state.showOverlays;
    },
    toggleBleed: (state) => {
      state.showBleed = !state.showBleed;
    },
    toggleSafeZone: (state) => {
      state.showSafeZone = !state.showSafeZone;
    },
    toggleCutLines: (state) => {
      state.showCutLines = !state.showCutLines;
    },

    // Text layer actions
    addTextLayer: (state, action) => {
      pushToHistory(state);
      const newLayer = {
        id: `text-${Date.now()}`,
        text: action.payload.text || 'New Text',
        fontFamily: action.payload.fontFamily || 'Cairo',
        fontSize: action.payload.fontSize || 24,
        fontWeight: action.payload.fontWeight || 'normal',
        color: action.payload.color || '#000000',
        alignment: action.payload.alignment || 'center',
        position: action.payload.position || { x: 0, y: 0, z: 0.5 },
        rotation: action.payload.rotation || { x: 0, y: 0, z: 0 },
        face: action.payload.face || 'front', // front, back, top, left, right
      };
      state.textLayers.push(newLayer);
      state.activeTextLayerId = newLayer.id;
    },
    updateTextLayer: (state, action) => {
      pushToHistory(state);
      const { id, ...updates } = action.payload;
      const layerIndex = state.textLayers.findIndex(l => l.id === id);
      if (layerIndex !== -1) {
        state.textLayers[layerIndex] = { ...state.textLayers[layerIndex], ...updates };
      }
    },
    removeTextLayer: (state, action) => {
      pushToHistory(state);
      state.textLayers = state.textLayers.filter(l => l.id !== action.payload);
      if (state.activeTextLayerId === action.payload) {
        state.activeTextLayerId = state.textLayers.length > 0 ? state.textLayers[0].id : null;
      }
    },
    setActiveTextLayer: (state, action) => {
      state.activeTextLayerId = action.payload;
    },

    // Camera preset
    setCameraPreset: (state, action) => {
      state.cameraPreset = action.payload;
    },

    // Template
    applyTemplate: (state, action) => {
      pushToHistory(state);
      const template = action.payload;
      state.selectedTemplate = template.id;

      // Apply template settings
      if (template.material) state.material = template.material;
      if (template.customColor) state.customColor = template.customColor;
      if (template.textLayers) {
        state.textLayers = template.textLayers.map((layer, idx) => ({
          ...layer,
          id: `text-${Date.now()}-${idx}`,
        }));
      }
      if (template.printArea) state.printArea = template.printArea;
    },

    // Undo/Redo actions
    undo: (state) => {
      if (state.historyIndex > 0) {
        state.historyIndex -= 1;
        const snapshot = state.history[state.historyIndex];
        restoreFromSnapshot(state, snapshot);
        state.canUndo = state.historyIndex > 0;
        state.canRedo = true;
      }
    },
    redo: (state) => {
      if (state.historyIndex < state.history.length - 1) {
        state.historyIndex += 1;
        const snapshot = state.history[state.historyIndex];
        restoreFromSnapshot(state, snapshot);
        state.canUndo = true;
        state.canRedo = state.historyIndex < state.history.length - 1;
      }
    },

    // Clear history
    clearHistory: (state) => {
      state.history = [getStateSnapshot(state)];
      state.historyIndex = 0;
      state.canUndo = false;
      state.canRedo = false;
    },

    loadDesign: (state, action) => {
      // Reset history when loading a design
      const newState = { ...defaultState, ...action.payload, isModified: false };
      Object.assign(state, newState);
      state.history = [getStateSnapshot(state)];
      state.historyIndex = 0;
      state.canUndo = false;
      state.canRedo = false;
    },

    resetEditor: () => {
      return {
        ...initialState,
        history: [getStateSnapshot(defaultState)],
        historyIndex: 0,
      };
    },
  },
});

export const {
  setProduct,
  setPosition,
  setRotation,
  setScale,
  setMaterial,
  setCustomColor,
  setPrintArea,
  setDesignFile,
  setValidationResults,
  toggleValidation,
  toggleOverlays,
  toggleBleed,
  toggleSafeZone,
  toggleCutLines,
  addTextLayer,
  updateTextLayer,
  removeTextLayer,
  setActiveTextLayer,
  setCameraPreset,
  applyTemplate,
  undo,
  redo,
  clearHistory,
  loadDesign,
  resetEditor,
} = editorSlice.actions;

export default editorSlice.reducer;
